import { useEffect, useState } from "react";
import axios from "axios";
import { Copy, XCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import config from "../config";

const OrdersTable = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [confirmData, setConfirmData] = useState({
    show: false,
    order: null,
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        `${config.API_BASE_URL}/api/auth/fetchOrders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data.success) {
        setOrders(res.data.orders);
      } else {
        toast.error("Failed to fetch orders");
      }
    } catch (err) {
      console.error("❌ Error fetching orders:", err);
      toast.error("Error fetching orders");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 Fetch on mount
  useEffect(() => {
    fetchOrders();
  }, []);

  // 🔥 Poll every 5 seconds WITHOUT reloading the page
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        // Get the token from localStorage for every request
        const token = localStorage.getItem("authToken");

        const res = await axios.get(
          `${config.API_BASE_URL}/api/auth/fetchOrders`,
          {
            headers: {
              Authorization: `Bearer ${token}`, // Add the Bearer token
              "Content-Type": "application/json",
            },
          },
        );

        const updatedOrders = res.data.orders;

        // If status changed -> update UI
        setOrders((prevOrders) => {
          const changed =
            JSON.stringify(prevOrders) !== JSON.stringify(updatedOrders);
          return changed ? updatedOrders : prevOrders;
        });
      } catch (err) {
        console.error("❌ Error checking for webhook update:", err);
        // Optional: Handle token expiration by redirecting to login
        if (err.response?.status === 401) {
          console.log("Token expired during polling");
          // You could clear the token and redirect here
          // localStorage.removeItem("authToken");
          // navigate("/login");
        }
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, []); // This effect runs once on mount

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("📋 Number copied!");
  };

  const handleConfirmCancel = async () => {
    const order = confirmData.order;

    try {
      setCancelling(order.reference);

      const token = localStorage.getItem("authToken");
      const apiEndpoint = order.country
        ? `${config.API_BASE_URL}/api/auth/cancelOrder`
        : `${config.API_BASE_URL}/api/auth/cancelUSOrder`;

      const res = await axios.post(
        apiEndpoint,
        { reference: order.reference },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        toast.success("Order cancelled and refund processed!");
        fetchOrders();
      } else {
        toast.error(res.data.message || "Failed to cancel order");
      }
    } catch (error) {
      toast.error("Error cancelling order");
    } finally {
      setConfirmData({ show: false, order: null });
      setCancelling(null);
    }
  };

  return (
    <>
      {/* MODAL FOR CANCEL ORDER */}
      {confirmData.show && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80 text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              Cancel Order
            </h2>

            <p className="text-gray-700 mb-6">
              Are you sure you want to cancel this order?
            </p>

            <div className="flex justify-between gap-3">
              <button
                onClick={() => setConfirmData({ show: false, order: null })}
                className="flex-1 py-2 rounded-md border border-gray-300 text-gray-700"
              >
                No
              </button>

              <button
                onClick={handleConfirmCancel}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md mt-[60px] lg:mt-10">
        <h2 className="text-xl sm:text-2xl font-bold mb-5 text-gray-900">
          Your Orders
        </h2>

        {loading ? (
          <p className="text-gray-700 text-center">Loading orders...</p>
        ) : orders.length === 0 ? (
          <p className="text-gray-700 text-center">No orders yet.</p>
        ) : (
          <>
            {/* 🖥️ Table view for larger screens */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm text-left text-gray-800 border border-gray-200 rounded-lg">
                <thead className="bg-gray-100 text-gray-900">
                  <tr>
                    <th className="px-4 py-2 border-b">Country</th>
                    <th className="px-4 py-2 border-b">Service</th>
                    <th className="px-4 py-2 border-b">Phone Number</th>
                    <th className="px-4 py-2 border-b">Price</th>
                    <th className="px-4 py-2 border-b">Status</th>
                    <th className="px-4 py-2 border-b">Created</th>
                    <th className="px-4 py-2 border-b">Code</th>
                    <th className="px-4 py-2 border-b">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order._id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-2">{order.country}</td>
                      <td className="px-4 py-2">{order.serviceName}</td>
                      <td className="px-4 py-2 flex items-center gap-2">
                        <span>{order.phoneNumber}</span>
                        <Copy
                          size={16}
                          className="cursor-pointer text-blue-600 hover:text-blue-500"
                          onClick={() => copyToClipboard(order.phoneNumber)}
                        />
                      </td>
                      <td className="px-4 py-2 font-medium text-gray-900">
                        ₦{order.price?.toFixed(2)}
                      </td>
                      <td
                        className={`px-4 py-2 font-semibold ${
                          order.status === "completed"
                            ? "text-green-600"
                            : order.status === "active"
                              ? "text-yellow-600"
                              : order.status === "cancelled"
                                ? "text-red-600"
                                : "text-gray-600"
                        }`}
                      >
                        {order.status}
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {new Date(order.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {order.code || "—"}
                      </td>
                      <td className="px-4 py-2 space-x-2">
                        {order.status === "active" && !order.code && (
                          <button
                            onClick={() =>
                              setConfirmData({ show: true, order })
                            }
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs flex items-center gap-1"
                            disabled={cancelling === order.reference}
                          >
                            <XCircle size={14} />
                            {cancelling === order.reference
                              ? "Cancelling..."
                              : "Cancel Order"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 📱 Card view for mobile */}
            <div className="block md:hidden space-y-4">
              {orders.map((order) => (
                <div
                  key={order._id}
                  className="border border-gray-200 rounded-lg p-4 shadow-sm"
                >
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-semibold text-gray-900">
                      {order.serviceName} ({order.country})
                    </p>
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded ${
                        order.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : order.status === "active"
                            ? "bg-yellow-100 text-yellow-700"
                            : order.status === "cancelled"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {order.status}
                    </span>
                  </div>

                  <div className="text-sm text-gray-700 space-y-1">
                    <p>
                      <span className="font-semibold">Phone:</span>{" "}
                      {order.phoneNumber}
                      <Copy
                        size={14}
                        className="inline ml-1 cursor-pointer text-blue-600 hover:text-blue-500"
                        onClick={() => copyToClipboard(order.phoneNumber)}
                      />
                    </p>
                    <p>
                      <span className="font-semibold">Price:</span> ₦
                      {order.price?.toFixed(2)}
                    </p>
                    <p>
                      <span className="font-semibold">Created:</span>{" "}
                      {new Date(order.createdAt).toLocaleString()}
                    </p>
                    <p>
                      <span className="font-semibold">Code:</span>{" "}
                      {order.code || "—"}
                    </p>
                  </div>

                  {order.status === "active" && !order.code && (
                    <button
                      onClick={() => setConfirmData({ show: true, order })}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs flex items-center gap-1"
                      disabled={cancelling === order.reference}
                    >
                      <XCircle size={14} />
                      {cancelling === order.reference
                        ? "Cancelling..."
                        : "Cancel Order"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default OrdersTable;
